/**
 * useMonthlyShipments — 특정 연도의 1~12월 출하 집계.
 *
 * documentId() range query로 연간 docs 가져온 뒤 client-side로 월별 누적.
 * year별 in-memory 캐시 — 같은 year 재요청 시 0 reads.
 */
import { useEffect, useState } from "react";
import { useDataTenant } from "@/hooks/useTenantPath";
import type { TenantKey } from "@/domain/space";
import { dailyShipmentsRepo } from "@/services/daily_shipments.repository";

export interface MonthlyBucket {
  /** YYYY-MM */
  ym: string;
  /** 1~12 */
  month: number;
  govM3: number;
  custM3: number;
  totalM3: number;
}

/**
 * 캐시 키 = "법인:연도" — 법인 전환 시 다른 법인 집계가 섞이지 않게(2026-10-20 1단계).
 * ⚠️ 지금 조회(dailyShipmentsRepo)는 법인 구분 없는 중앙 컬렉션이라 경원 키에도 중앙 숫자가 담긴다(경원 공간은 "데이터 미연결" 안내 띠).
 *    2단계(tenants/{법인}/… 이전)에서 조회에 t 를 넘기면 키는 그대로 쓴다.
 */
const cache = new Map<string, MonthlyBucket[]>();

function initBuckets(year: number): MonthlyBucket[] {
  const arr: MonthlyBucket[] = [];
  for (let m = 1; m <= 12; m++) {
    arr.push({
      ym: `${year}-${String(m).padStart(2, "0")}`,
      month: m,
      govM3: 0,
      custM3: 0,
      totalM3: 0,
    });
  }
  return arr;
}

export interface UseMonthlyShipmentsState {
  monthly: MonthlyBucket[];
  loading: boolean;
  error: string | null;
}

export function useMonthlyShipments(year: number, tenant?: TenantKey): UseMonthlyShipmentsState {
  const dataTenant = useDataTenant();
  const t = tenant ?? dataTenant;
  const ck = `${t}:${year}`;
  // state 에 법인을 함께 둔다 — 법인이 바뀐 직후(새 조회 전) 이전 법인 숫자를 내보내지 않게
  const [state, setState] = useState<{ t: TenantKey; data: MonthlyBucket[] }>(
    () => ({ t, data: cache.get(ck) ?? initBuckets(year) }),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // year <= 0 → 비활성 (권한 게이팅 sentinel). fetch 안 함 (permission-denied 회피).
    if (year <= 0) {
      setState({ t, data: initBuckets(year) });
      return;
    }
    const cached = cache.get(ck);
    if (cached) {
      setState({ t, data: cached });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    dailyShipmentsRepo
      .listByYear(year)
      .then((docs) => {
        if (cancelled) return;
        const buckets = initBuckets(year);
        for (const d of docs) {
          const mm = parseInt(d.id.slice(5, 7), 10);
          if (!mm || mm < 1 || mm > 12) continue;
          const bucket = buckets[mm - 1];
          bucket.govM3 += d.gov_m3;
          bucket.custM3 += d.cust_m3;
          bucket.totalM3 += d.total_m3;
        }
        // 소수 1자리 반올림
        for (const b of buckets) {
          b.govM3 = Math.round(b.govM3 * 10) / 10;
          b.custM3 = Math.round(b.custM3 * 10) / 10;
          b.totalM3 = Math.round(b.totalM3 * 10) / 10;
        }
        cache.set(ck, buckets);
        setState({ t, data: buckets });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, ck, t]);

  const monthly = state.t === t ? state.data : initBuckets(year);
  return { monthly, loading, error };
}
